import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Locale,
  SiteContext,
  TranslationEstimate,
  TranslationJobSummary,
  TranslationOverview,
  TranslationStrategy,
  TranslationTargetKind,
} from '@icetus/contracts';
import { LOCALE_LABEL, MAX_JOB_CHARS, translateQuotaForPlan } from '@icetus/contracts';
import { Badge, Button, Card, quotaTone } from '@icetus/ui';
import {
  cancelTranslationJob,
  createTranslationJob,
  estimateTranslation,
  fetchSites,
  fetchTranslationOverview,
  publishTranslation,
} from '../api';
import { useConfirm } from '../components/confirm';
import { Modal } from '../components/overlay';
import { TranslationReviewPage } from './translation-review-page';
import { planLabel } from '../lib/plan-label';

/**
 * 多语言翻译：总览 + 向导 + 校对入口。
 *
 * 这一页要回答客户三个问题，顺序不能乱：
 *   1. 我现在几种语言、每种翻到哪了（卡片）
 *   2. 还能翻多少（额度条）—— 因为下一个动作要花钱
 *   3. 下一步做什么（每张卡片上只有一个主操作）
 *
 * 刻意不做的事：
 *   · 不在这里列「译文逐条对照」，那是校对页的活（一页几百段会把总览淹掉）
 *   · 不在卡片上放「删除这门语言」，那是站点设置里的事（这里只管翻译）
 *   · 不做「一键全站翻译」的大按钮：任务按语言串行、成本按语言结算，
 *     一次点下去同时开三种语言，失败时客户不知道该重试哪一个
 */

const KIND_LABEL: Record<TranslationTargetKind, string> = {
  page: '页面区块',
  product: '商品',
  category: '分类',
  shell: '页头页脚',
};

const KIND_HINT: Record<TranslationTargetKind, string> = {
  page: '首屏、关于我们、FAQ 这些页面里的标题与正文。',
  product: '商品名称、卖点、详情与规格值。型号与数字会原样保留。',
  category: '分类名称与描述。',
  shell: 'Logo 旁的标语、主导航菜单、页脚栏目与版权行。',
};

/**
 * 策略。
 *
 * 三个选项的差别是「会不会重花一遍钱」，所以文案直接说清楚会翻哪些，
 * 而不是给一句「智能匹配」让客户自己猜。
 */
const STRATEGY_LABEL: Record<TranslationStrategy, { title: string; hint: string }> = {
  missing: {
    title: '只补没翻过的',
    hint: '首次翻译选这个。已经翻过的段落一个字都不会重花。',
  },
  stale: {
    title: '补没翻的 + 重翻改过的',
    hint: '日常用这个：中文原文改过、译文还是旧版的段落会被重翻。',
  },
  all: {
    title: '全部重翻',
    hint: '只有在整体质量不满意时才用。人工改过的译文默认仍会保留。',
  },
};

export function TranslationPage({
  tenantId,
  canRun,
}: {
  tenantId: string;
  /** 有没有 `translation:run`。没有就藏掉"发起翻译"的入口，只留看与校对 */
  canRun: boolean;
}) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [overview, setOverview] = useState<TranslationOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** 向导：preset 决定打开时预选哪几门语言（卡片上的「翻译」只带一门） */
  const [wizard, setWizard] = useState<{ locales: Locale[]; strategy: TranslationStrategy } | null>(
    null,
  );
  /** 校对页：有值就整页替换（不叠 Modal —— 校对要长期停留，做成浮层会被误关） */
  const [review, setReview] = useState<Locale | null>(null);

  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    let cancelled = false;
    fetchSites(tenantId)
      .then((items) => {
        if (cancelled) return;
        setSites(items);
        setSiteId((prev) =>
          items.some((s) => s.site.id === prev) ? prev : (items[0]?.site.id ?? ''),
        );
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const load = useCallback(
    async (quiet = false) => {
      if (!siteId) return;
      try {
        const next = await fetchTranslationOverview(tenantId, siteId);
        setOverview(next);
        setError(null);
      } catch (err: unknown) {
        if (!quiet) setError(err instanceof Error ? err.message : String(err));
      }
    },
    [tenantId, siteId],
  );

  useEffect(() => {
    setOverview(null);
    setNotice(null);
    void load();
  }, [load]);

  /**
   * 有任务在跑时轮询。
   *
   * 用 3 秒而不是 WebSocket：一个站点的翻译任务最长十几分钟，
   * 而客户在这一页停留的时间通常不到两分钟 —— 为这点交互接一条长连接，
   * 换来的是 nginx 超时配置、断线重连、多标签页重复订阅三件麻烦事。
   * 没有活动任务时**不轮询**，切走就停。
   */
  const hasActive = (overview?.locales ?? []).some((l) => l.activeJob !== null);
  useEffect(() => {
    if (!hasActive) return;
    const timer = window.setInterval(() => void load(true), 3000);
    return () => window.clearInterval(timer);
  }, [hasActive, load]);

  /**
   * 发起一个语言的任务。
   *
   * 返回是否成功，**不吞异常**：向导要据此决定"还跑不跑下一门语言"。
   * 吞掉的话 provider 挂了会让三门语言各失败一次，客户看到三行红字却不知道
   * 其实是同一个原因。
   */
  const startJob = async (
    locale: Locale,
    scope: { kinds: TranslationTargetKind[]; strategy: TranslationStrategy; forceHuman: boolean },
  ): Promise<boolean> => {
    setBusy(true);
    try {
      await createTranslationJob(tenantId, { siteId, targetLocale: locale, ...scope });
      setNotice(`${LOCALE_LABEL[locale]}已开始翻译。可以关掉这个页面，任务在服务端继续跑。`);
      await load(true);
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async (job: TranslationJobSummary) => {
    const ok = await confirm({
      title: '停止这次翻译？',
      message: `已经翻好的 ${job.doneUnits} 段会保留，不会回滚。剩下的段落下次要重新发起。`,
      confirmText: '停止',
      cancelText: '继续跑',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await cancelTranslationJob(tenantId, job.id);
      await load(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onPublish = async (locale: Locale, pages: number) => {
    const ok = await confirm({
      title: `发布 ${LOCALE_LABEL[locale]} 的 ${pages} 个页面？`,
      message:
        '发布后这些页面会出现在前台与 sitemap 里。还有没人工校对过的段落也能现在发 —— 之后校对改完会立刻生效，不用再点一次发布。',
      confirmText: '发布',
      cancelText: '再等等',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const result = await publishTranslation(tenantId, siteId, locale);
      setNotice(`已发布 ${result.published} 个页面。`);
      await load(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (review) {
    return (
      <TranslationReviewPage
        tenantId={tenantId}
        siteId={siteId}
        locale={review}
        onBack={() => {
          setReview(null);
          void load(true);
        }}
        onChanged={() => void load(true)}
      />
    );
  }

  return (
    <section className="stack">
      <div className="tablebar">
        <label className="picker">
          <span>站点</span>
          <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {sites.map((s) => (
              <option key={s.site.id} value={s.site.id}>
                {s.site.name}
              </option>
            ))}
          </select>
        </label>
        <div className="tablebar__right">
          <Button
            variant="ghost"
            disabled={!canRun || !overview || overview.locales.length === 0}
            title={canRun ? undefined : '你的角色可以校对，但不能发起翻译（会花钱）'}
            onClick={() =>
              setWizard({
                locales: (overview?.locales ?? []).map((l) => l.locale),
                strategy: 'missing',
              })
            }
          >
            批量翻译…
          </Button>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice notice--ok">{notice}</p> : null}

      {!overview ? (
        <p className="muted">加载中…</p>
      ) : overview.locales.length === 0 ? (
        <Card title="还没有开启第二种语言">
          <p className="muted">
            翻译的前提是站点先开通目标语言。开通之后，这里会出现每种语言的翻译进度，
            以及「开始翻译」的入口。
          </p>
          <p className="muted">
            现在默认语言是 <strong>{LOCALE_LABEL[overview.defaultLocale]}</strong>，
            其余三门的开关在「站点信息 → 语言」。
          </p>
        </Card>
      ) : (
        <>
          <QuotaBar overview={overview} />

          <div className="grid">
            {overview.locales.map((status) => (
              <LocaleCard
                key={status.locale}
                status={status}
                quotaLeft={quotaLeftOf(overview)}
                canRun={canRun}
                busy={busy}
                onTranslate={() =>
                  setWizard({ locales: [status.locale], strategy: pickStrategy(status) })
                }
                onReview={() => setReview(status.locale)}
                onPublish={() => void onPublish(status.locale, status.draftPages)}
                onCancel={() => (status.activeJob ? void onCancel(status.activeJob) : undefined)}
              />
            ))}
          </div>

          <RecentJobs jobs={overview.recentJobs} />
        </>
      )}

      {wizard && siteId ? (
        <TranslationWizard
          tenantId={tenantId}
          siteId={siteId}
          defaultLocale={overview?.defaultLocale ?? 'en'}
          presetLocales={wizard.locales}
          presetStrategy={wizard.strategy}
          quotaLeft={overview ? quotaLeftOf(overview) : 0}
          onClose={() => setWizard(null)}
          onStart={startJob}
          onDone={async () => {
            setWizard(null);
            await load(true);
          }}
        />
      ) : null}

      {confirmDialog}
    </section>
  );
}

// ── 额度 ─────────────────────────────────────────────────────────────

/**
 * 剩余额度。
 *
 * `quota === 0` 表示不限量（企业定制合同），这时不能算「剩余 0」——
 * 那会让向导把每一次翻译都判成超额。
 */
function quotaLeftOf(overview: TranslationOverview): number {
  if (overview.usage.quota <= 0) return Number.POSITIVE_INFINITY;
  return Math.max(0, overview.usage.quota - overview.usage.charsThisMonth);
}

/**
 * 卡片上「翻译」按钮的默认策略。
 *
 * 一条规则：有译文过期 → stale（最省且能修好）；一段都没有 → missing；
 * 已经全翻且不过期 → 客户还点，那就是想重翻，给 all 但向导里会提示会花钱。
 */
function pickStrategy(status: TranslationOverview['locales'][number]): TranslationStrategy {
  if (status.done === 0) return 'missing';
  if (status.stale > 0) return 'stale';
  return 'all';
}

function QuotaBar({ overview }: { overview: TranslationOverview }) {
  const { charsThisMonth, quota, planCode } = overview.usage;
  const unlimited = quota <= 0;
  const ratio = unlimited ? 0 : Math.min(1, charsThisMonth / quota);
  const tone = unlimited ? 'success' : quotaTone(charsThisMonth, quota);
  const percent = Math.round(ratio * 100);

  return (
    <Card
      title="本月翻译额度"
      description={
        unlimited
          ? '当前套餐不限翻译字符数。'
          : `按源文字符计费：中文一个字算一个，英文一个字母算一个。额度每月 1 日重置，不累积。`
      }
      actions={
        <Badge tone={tone}>
          {/* 超额时剩 0 而不是负数：客户看到的应该是"用完了"，不是"-1.2 万字" */}
          {unlimited ? '不限量' : `剩 ${formatChars(Math.max(0, quota - charsThisMonth))}`}
        </Badge>
      }
    >
      {unlimited ? null : (
        <>
          <div className="meter" role="img" aria-label={`已用 ${percent}%`}>
            <div
              className={`meter__fill meter__fill--${tone}`}
              style={{ inlineSize: `${Math.max(percent, charsThisMonth > 0 ? 1 : 0)}%` }}
            />
          </div>
          <p className="muted meter__legend">
            已用 {formatChars(charsThisMonth)} / {formatChars(quota)}（{percent}%）
            <span className="dot-sep">·</span>
            套餐 {planLabel(planCode)}
            {quota === translateQuotaForPlan(planCode) ? null : (
              <>
                <span className="dot-sep">·</span>
                额度为定制值
              </>
            )}
          </p>
        </>
      )}
    </Card>
  );
}

function formatChars(value: number): string {
  if (!Number.isFinite(value)) return '∞';
  if (Math.abs(value) >= 10_000) return `${(value / 10_000).toFixed(1)} 万字`;
  return `${value.toLocaleString('zh-CN')} 字`;
}

// ── 语言卡片 ─────────────────────────────────────────────────────────

function LocaleCard({
  status,
  quotaLeft,
  canRun,
  busy,
  onTranslate,
  onReview,
  onPublish,
  onCancel,
}: {
  status: TranslationOverview['locales'][number];
  quotaLeft: number;
  canRun: boolean;
  busy: boolean;
  onTranslate: () => void;
  onReview: () => void;
  onPublish: () => void;
  onCancel: () => void;
}) {
  const { locale, done, total, stale, draftPages, activeJob } = status;
  const ratio = total > 0 ? done / total : 0;
  const complete = total > 0 && done >= total && stale === 0;
  const running = activeJob !== null;

  /**
   * 状态只有四种，且互斥。用一句话说清「现在是什么情况」，
   * 而不是让客户从三个数字里自己推断。
   */
  const headline = running
    ? '正在翻译…'
    : total === 0
      ? '源语言还没有可翻译的内容'
      : done === 0
        ? '还没有翻译'
        : complete
          ? '已全部翻译'
          : stale > 0
            ? '有内容改过，译文需要更新'
            : '部分内容还没翻';

  const tone = running ? 'warn' : complete ? 'success' : done === 0 ? 'neutral' : 'warn';

  return (
    <Card
      title={LOCALE_LABEL[locale]}
      description={headline}
      actions={
        <Badge tone={tone}>
          {running ? '进行中' : complete ? '完成' : `${Math.round(ratio * 100)}%`}
        </Badge>
      }
    >
      {running ? (
        <JobProgress job={activeJob} onCancel={onCancel} />
      ) : (
        <>
          <div className="meter meter--slim">
            <div
              className={`meter__fill meter__fill--${complete ? 'success' : 'warn'}`}
              style={{ inlineSize: `${Math.round(ratio * 100)}%` }}
            />
          </div>
          <dl className="langstat">
            <div>
              <dt>已翻段落</dt>
              <dd>
                {done.toLocaleString('zh-CN')} / {total.toLocaleString('zh-CN')}
              </dd>
            </div>
            <div>
              <dt>待重翻（原文改过）</dt>
              <dd className={stale > 0 ? 'langstat__warn' : undefined}>{stale}</dd>
            </div>
            <div>
              <dt>未发布页面</dt>
              <dd className={draftPages > 0 ? 'langstat__warn' : undefined}>{draftPages}</dd>
            </div>
          </dl>
        </>
      )}

      <div className="card-actions">
        {running || !canRun ? null : (
          <Button
            variant={done === 0 ? 'primary' : 'ghost'}
            disabled={busy || total === 0}
            onClick={onTranslate}
          >
            {done === 0 ? '开始翻译' : '继续翻译'}
          </Button>
        )}
        <Button variant="subtle" disabled={done === 0} onClick={onReview}>
          校对
        </Button>
        {draftPages > 0 ? (
          <Button
            variant={done > 0 && complete ? 'primary' : 'subtle'}
            disabled={busy}
            onClick={onPublish}
          >
            发布 {draftPages} 页
          </Button>
        ) : null}
      </div>

      {/*
       * 额度不够时提前拦。
       *
       * 不拦的话客户点进去、向导走完、任务创建成功，然后在跑的过程中
       * 撞到 quota_exceeded 变成 partial —— 那时钱已经花掉一部分了。
       */}
      {!running && done < total && quotaLeft <= 0 ? (
        <p className="muted card-note">本月额度已用完，下月 1 日重置。需要更多请联系我们。</p>
      ) : null}
    </Card>
  );
}

/**
 * 任务进度。
 *
 * 只有三个数字有意义：已翻 / 待复核 / 失败。刻意不显示 ETA ——
 * 模型响应时间波动很大，一个跳来跳去的「还剩 40 秒」比不显示更让人焦虑。
 */
function JobProgress({ job, onCancel }: { job: TranslationJobSummary; onCancel: () => void }) {
  const settled = job.doneUnits + job.reviewUnits + job.failedUnits;
  const ratio = job.totalUnits > 0 ? settled / job.totalUnits : 0;
  return (
    <>
      <div className="meter meter--slim">
        <div
          className="meter__fill meter__fill--warn"
          style={{ inlineSize: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      <dl className="langstat">
        <div>
          <dt>已翻</dt>
          <dd>
            {settled} / {job.totalUnits}
          </dd>
        </div>
        <div>
          <dt>待复核</dt>
          <dd className={job.reviewUnits > 0 ? 'langstat__warn' : undefined}>{job.reviewUnits}</dd>
        </div>
        <div>
          <dt>失败</dt>
          <dd className={job.failedUnits > 0 ? 'langstat__warn' : undefined}>{job.failedUnits}</dd>
        </div>
      </dl>
      <div className="card-actions">
        <Button variant="subtle" onClick={onCancel} disabled={job.cancelRequested}>
          {job.cancelRequested ? '正在停止…' : '停止'}
        </Button>
      </div>
    </>
  );
}

// ── 最近任务 ─────────────────────────────────────────────────────────

function RecentJobs({ jobs }: { jobs: TranslationJobSummary[] }) {
  if (jobs.length === 0) return null;
  return (
    <Card
      title="最近的翻译任务"
      description="保留最近 10 条。已经结束的任务只用于回看，不影响现在的进度。"
    >
      <div className="tablewrap">
        <table className="table">
          <thead>
            <tr>
              <th>语言</th>
              <th>状态</th>
              <th className="table__num">段落</th>
              <th className="table__num">用量</th>
              <th>时间</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.id}>
                <td>{LOCALE_LABEL[job.targetLocale]}</td>
                <td>
                  <Badge tone={jobTone(job.status)}>{jobLabel(job)}</Badge>
                  {job.error ? <p className="muted job-error">{job.error}</p> : null}
                </td>
                <td className="table__num">
                  {job.doneUnits + job.reviewUnits}
                  {job.failedUnits > 0 ? (
                    <span className="muted">（失败 {job.failedUnits}）</span>
                  ) : null}
                </td>
                {/*
                  用量 = 计入本月的字符数，是客户唯一需要关心的数字。
                  不显示模型名：换模型是我们的运维决定，客户看到的应该是
                  「这次用了多少额度」。
                */}
                <td className="table__num">{job.charsUsed.toLocaleString('zh-CN')} 字</td>
                <td className="muted">{shortTime(job.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function jobTone(
  status: TranslationJobSummary['status'],
): 'neutral' | 'success' | 'warn' | 'danger' {
  if (status === 'succeeded') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'partial') return 'warn';
  if (status === 'running' || status === 'pending') return 'warn';
  return 'neutral';
}

function jobLabel(job: TranslationJobSummary): string {
  switch (job.status) {
    case 'pending':
      return '排队中';
    case 'running':
      return job.cancelRequested ? '停止中' : '进行中';
    case 'succeeded':
      return '成功';
    case 'partial':
      return '部分完成';
    case 'failed':
      return '失败';
    case 'canceled':
      return '已停止';
    default:
      return job.status;
  }
}

function shortTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getMonth() + 1}/${date.getDate()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ── 向导 ─────────────────────────────────────────────────────────────

type WizardStep = 'scope' | 'estimate' | 'running';

/**
 * 三步向导：范围 → 花多少 → 跑。
 *
 * 为什么不把三步压成一屏：第二屏是**唯一**能让客户在花钱前叫停的地方。
 * 压成一屏的话，「预估」会变成一个随时在变的小字，客户根本不会看第二眼。
 */
function TranslationWizard({
  tenantId,
  siteId,
  defaultLocale,
  presetLocales,
  presetStrategy,
  quotaLeft,
  onClose,
  onStart,
  onDone,
}: {
  tenantId: string;
  siteId: string;
  defaultLocale: Locale;
  presetLocales: Locale[];
  presetStrategy: TranslationStrategy;
  quotaLeft: number;
  onClose: () => void;
  /** 返回 false = 这一门没提交成功，向导就此停住 */
  onStart: (
    locale: Locale,
    scope: { kinds: TranslationTargetKind[]; strategy: TranslationStrategy; forceHuman: boolean },
  ) => Promise<boolean>;
  onDone: () => Promise<void>;
}) {
  const [step, setStep] = useState<WizardStep>('scope');
  const [kinds, setKinds] = useState<TranslationTargetKind[]>([
    'page',
    'product',
    'category',
    'shell',
  ]);
  const [strategy, setStrategy] = useState<TranslationStrategy>(presetStrategy);
  const [forceHuman, setForceHuman] = useState(false);
  const [locales, setLocales] = useState<Locale[]>(presetLocales);
  const [estimates, setEstimates] = useState<TranslationEstimate[] | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  /**
   * 串行跑：一次只开一门语言的任务。
   *
   * 并发开三个任务看着快，但一个 key 撞到 429 时三个一起失败，
   * 而额度是「每个任务各扣各的」，客户要分别重试三次。
   */
  const queueRef = useRef<Locale[]>([]);
  const [runningLocale, setRunningLocale] = useState<Locale | null>(null);

  const totalChars = useMemo(
    () => (estimates ?? []).reduce((sum, e) => sum + e.chars, 0),
    [estimates],
  );
  const overQuota = totalChars > quotaLeft;
  const overSingle = (estimates ?? []).some((e) => e.chars > MAX_JOB_CHARS);
  const nothingToDo = (estimates ?? []).every((e) => e.units === 0);

  const runEstimate = async () => {
    setEstimating(true);
    setError(null);
    try {
      const result = await estimateTranslation(tenantId, {
        siteId,
        targetLocales: locales,
        kinds,
        strategy,
        forceHuman,
      });
      setEstimates(result);
      setStep('estimate');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setEstimating(false);
    }
  };

  /** 逐门语言提交，前一个结束后再起下一个 */
  const begin = async () => {
    setStarting(true);
    setError(null);
    queueRef.current = [...locales];
    setStep('running');
    await runNext();
    setStarting(false);
  };

  const runNext = async (): Promise<void> => {
    const next = queueRef.current.shift();
    if (!next) {
      setRunningLocale(null);
      return;
    }
    setRunningLocale(next);
    const ok = await onStart(next, { kinds, strategy, forceHuman });
    if (!ok) {
      /**
       * 一门失败就整队停。
       *
       * 继续跑下去只会让每一门都撞同一个错（key 失效、额度用完、provider 挂了），
       * 客户要对着三条一模一样的报错逐个判断。清空队列，让人先修好那一个原因。
       */
      queueRef.current = [];
      setRunningLocale(null);
      setError(`提交 ${LOCALE_LABEL[next]} 失败，后面的语言没有继续提交。`);
      return;
    }
    await runNext();
  };

  const toggleLocale = (locale: Locale) => {
    setLocales((prev) =>
      prev.includes(locale) ? prev.filter((l) => l !== locale) : [...prev, locale],
    );
    setEstimates(null);
  };

  const toggleKind = (kind: TranslationTargetKind) => {
    setKinds((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));
    setEstimates(null);
  };

  const footer =
    step === 'scope' ? (
      <>
        <Button variant="ghost" onClick={onClose}>
          取消
        </Button>
        <Button
          variant="primary"
          disabled={estimating || locales.length === 0 || kinds.length === 0}
          onClick={() => void runEstimate()}
        >
          {estimating ? '估算中…' : '下一步：看看要翻多少'}
        </Button>
      </>
    ) : step === 'estimate' ? (
      <>
        <Button variant="ghost" onClick={() => setStep('scope')}>
          上一步
        </Button>
        <Button
          variant="primary"
          disabled={starting || nothingToDo || overSingle}
          onClick={() => void begin()}
        >
          {starting ? '提交中…' : `开始翻译（${formatChars(totalChars)}）`}
        </Button>
      </>
    ) : (
      <Button variant="primary" onClick={() => void onDone()}>
        完成
      </Button>
    );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="翻译到其它语言"
      description={`从 ${LOCALE_LABEL[defaultLocale]} 翻译。译文会先写成草稿，你校对完再发布。`}
      meta={
        <span className="muted">
          第 {step === 'scope' ? 1 : step === 'estimate' ? 2 : 3} / 3 步
        </span>
      }
      footer={footer}
    >
      {error ? <p className="error">{error}</p> : null}

      {step === 'scope' ? (
        <div className="stack">
          <div className="field-block">
            <span>翻译成哪几种语言</span>
            <div className="chips">
              {(['zh', 'en', 'es', 'ar'] as Locale[])
                .filter((l) => l !== defaultLocale)
                .map((locale) => (
                  <label key={locale} className="check">
                    <input
                      type="checkbox"
                      checked={locales.includes(locale)}
                      onChange={() => toggleLocale(locale)}
                    />
                    <span>{LOCALE_LABEL[locale]}</span>
                  </label>
                ))}
            </div>
          </div>

          <div className="field-block">
            <span>翻哪些内容</span>
            <div className="chips">
              {(Object.keys(KIND_LABEL) as TranslationTargetKind[]).map((kind) => (
                <label key={kind} className="check" title={KIND_HINT[kind]}>
                  <input
                    type="checkbox"
                    checked={kinds.includes(kind)}
                    onChange={() => toggleKind(kind)}
                  />
                  <span>{KIND_LABEL[kind]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="field-block">
            <span>翻哪些段落</span>
            <div className="choices">
              {(Object.keys(STRATEGY_LABEL) as TranslationStrategy[]).map((key) => (
                <label key={key} className={`choice${strategy === key ? ' is-active' : ''}`}>
                  <input
                    type="radio"
                    name="translation-strategy"
                    checked={strategy === key}
                    onChange={() => {
                      setStrategy(key);
                      setEstimates(null);
                    }}
                  />
                  <span className="choice__body">
                    <strong>{STRATEGY_LABEL[key].title}</strong>
                    <span className="muted">{STRATEGY_LABEL[key].hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <label className="check">
            <input
              type="checkbox"
              checked={forceHuman}
              onChange={(e) => {
                setForceHuman(e.target.checked);
                setEstimates(null);
              }}
            />
            <span>
              连我手工改过的译文一起覆盖
              <span className="muted">（默认不覆盖 —— 那些是你花时间改的）</span>
            </span>
          </label>
        </div>
      ) : step === 'estimate' ? (
        <div className="stack">
          {estimates && estimates.length > 0 ? (
            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>语言</th>
                    <th className="table__num">段落</th>
                    <th className="table__num">字符</th>
                    <th className="table__num">跳过（人工改过）</th>
                    <th className="table__num">跳过（草稿页）</th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.map((estimate) => (
                    <tr key={estimate.targetLocale}>
                      <td>{LOCALE_LABEL[estimate.targetLocale]}</td>
                      <td className="table__num">{estimate.units}</td>
                      <td className="table__num">{estimate.chars.toLocaleString('zh-CN')}</td>
                      <td className="table__num muted">{estimate.skipHuman || '—'}</td>
                      <td className="table__num muted">{estimate.skipDraftPages || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <dl className="kv">
            <div>
              <dt>这次会用掉</dt>
              <dd>{formatChars(totalChars)}</dd>
            </div>
            <div>
              <dt>剩余额度</dt>
              <dd className={overQuota ? 'langstat__warn' : undefined}>{formatChars(quotaLeft)}</dd>
            </div>
          </dl>

          {nothingToDo ? (
            <p className="notice">
              按现在的选择，没有需要翻译的段落。可能都已经翻过了 —— 换个策略试试。
            </p>
          ) : null}
          {overSingle ? (
            <p className="error">
              单门语言超过 {formatChars(MAX_JOB_CHARS)}，请减少内容类别或分批翻译。
              一次跑太大的任务，中途失败要整批重来。
            </p>
          ) : null}
          {overQuota && !overSingle ? (
            <p className="notice notice--warn">
              这次会超出本月剩余额度。可以先翻一部分（减少内容类别），或者等下月 1 日额度过期重置。
            </p>
          ) : null}
          {/*
           * 超出额度只提示、不拦。
           *
           * 服务端在建任务时会按实际额度截断并把任务标成 partial ——
           * 硬拦会让「还差 200 字」的客户完全没法继续；放行则至少能翻完大部分。
           */}
          <p className="muted">
            字符数按原文计算，四舍五入到整段。实际消耗以任务结束后的统计为准（个别段落可能翻译失败）。
          </p>
        </div>
      ) : (
        <div className="stack">
          <p>
            {runningLocale
              ? `正在提交 ${LOCALE_LABEL[runningLocale]}…`
              : queueRef.current.length > 0
                ? '继续提交下一门语言…'
                : '已全部提交。任务在服务端继续跑，可以关掉这个窗口。'}
          </p>
          <ul className="steps">
            {locales.map((locale) => {
              const estimate = estimates?.find((e) => e.targetLocale === locale);
              return (
                <li key={locale}>
                  {LOCALE_LABEL[locale]}
                  <span className="muted">
                    {runningLocale === locale
                      ? ' — 提交中'
                      : ` — ${estimate?.chars ?? 0} 字，已排队`}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="muted">
            进度可以在总览页的语言卡片上看。翻译完成后先去「校对」过一遍再发布。
          </p>
        </div>
      )}
    </Modal>
  );
}
